import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Wallet, Save, Eye, EyeOff, Zap, RefreshCw } from "lucide-react";

export default function AdminFaucetPay() {
  const [settings, setSettings] = useState({ api_key_masked: "", has_key: false, default_currency: "BTC", auto_withdraw: false });
  const [apiKey, setApiKey] = useState("");
  const [currency, setCurrency] = useState("BTC");
  const [autoWithdraw, setAutoWithdraw] = useState(false);
  const [fpEnabled, setFpEnabled] = useState(false);
  const [fpLoginEnabled, setFpLoginEnabled] = useState(false);
  const [fpRegisterEnabled, setFpRegisterEnabled] = useState(false);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState("");
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    API.get("/admin/faucetpay/settings").then(r => {
      setSettings(r.data);
      setCurrency(r.data.default_currency || "BTC");
      setAutoWithdraw(r.data.auto_withdraw || false);
      setFpEnabled(r.data.enabled || false);
      setFpLoginEnabled(r.data.login_enabled || false);
      setFpRegisterEnabled(r.data.register_enabled || false);
    }).catch(() => {});
  }, []);

  const saveSettings = async () => {
    setLoading("save");
    try {
      const body = { default_currency: currency, auto_withdraw: autoWithdraw, enabled: fpEnabled, login_enabled: fpLoginEnabled, register_enabled: fpRegisterEnabled };
      if (apiKey) body.api_key = apiKey;
      await API.put("/admin/faucetpay/settings", body);
      toast.success("FaucetPay settings saved");
      setApiKey("");
      const { data } = await API.get("/admin/faucetpay/settings");
      setSettings(data);
    } catch { toast.error("Failed to save"); }
    finally { setLoading(""); }
  };

  const loadBalances = async () => {
    setLoading("balance");
    try {
      const { data } = await API.get("/faucetpay/balance");
      if (!data.configured) {
        toast.error("FaucetPay API key not set");
        return;
      }
      setBalances(data.balances);
    } catch { toast.error("Failed to fetch balances"); }
    finally { setLoading(""); }
  };

  const CURRENCIES = ["BTC", "ETH", "LTC", "DOGE", "TRX", "USDT", "BNB", "SOL", "MATIC", "ZEC"];

  return (
    <AdminLayout title="FaucetPay Integration">
      <div className="space-y-6 max-w-4xl" data-testid="admin-faucetpay">
        {/* API Key Settings */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#FFD600]/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#FFD600]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#F8FAFC]">FaucetPay API Configuration</h3>
              <p className="text-xs text-[#64748B]">
                Status: {settings.has_key ? <span className="text-[#10B981]">Connected ({settings.api_key_masked})</span> : <span className="text-[#EF4444]">Not configured</span>}
              </p>
            </div>
          </div>

          <div>
            <Label className="text-[#94A3B8] text-sm">API Key {settings.has_key && "(leave blank to keep current)"}</Label>
            <div className="flex gap-2 mt-1">
              <Input type={showKey ? "text" : "password"} value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder={settings.has_key ? "Enter new key to update..." : "Paste your FaucetPay API key"}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC]" data-testid="faucetpay-api-key-input" />
              <button onClick={() => setShowKey(!showKey)} className="p-2 text-[#64748B] hover:text-[#94A3B8] border border-[#2A3241] rounded-lg">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#64748B] mt-1">Get your key from faucetpay.io &rarr; Dashboard &rarr; API section</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-[#94A3B8] text-sm">Default Currency</Label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="faucetpay-currency"
                className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={() => setFpEnabled(!fpEnabled)} data-testid="toggle-faucetpay-enabled"
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg border transition-all ${fpEnabled ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]"}`}>
                FaucetPay: {fpEnabled ? "ENABLED" : "DISABLED"}
              </button>
            </div>
            <div className="flex items-end">
              <button onClick={() => setAutoWithdraw(!autoWithdraw)} data-testid="toggle-auto-withdraw"
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg border transition-all ${autoWithdraw ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#2A3241] text-[#64748B] hover:text-[#94A3B8]"}`}>
                <Zap className="w-4 h-4" />
                Auto-Withdraw: {autoWithdraw ? "ON" : "OFF"}
              </button>
            </div>
          </div>

          <div className="flex gap-3 mt-2">
            <button onClick={() => setFpLoginEnabled(!fpLoginEnabled)} data-testid="toggle-faucetpay-login"
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg border transition-all ${fpLoginEnabled ? "border-[#B026FF]/40 bg-[#B026FF]/10 text-[#B026FF]" : "border-[#2A3241] text-[#64748B] hover:text-[#94A3B8]"}`}>
              FaucetPay Login: {fpLoginEnabled ? "ENABLED" : "DISABLED"}
            </button>
            <button onClick={() => setFpRegisterEnabled(!fpRegisterEnabled)} data-testid="toggle-faucetpay-register"
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg border transition-all ${fpRegisterEnabled ? "border-[#B026FF]/40 bg-[#B026FF]/10 text-[#B026FF]" : "border-[#2A3241] text-[#64748B] hover:text-[#94A3B8]"}`}>
              FaucetPay Register: {fpRegisterEnabled ? "ENABLED" : "DISABLED"}
            </button>
          </div>
          <p className="text-xs text-[#64748B]">When disabled, users can only link FaucetPay from their Profile for withdrawals.</p>

          <p className="text-xs text-[#94A3B8]">
            When auto-withdraw is ON, approved withdrawals are automatically sent via FaucetPay.
          </p>

          <button onClick={saveSettings} disabled={loading === "save"} data-testid="save-faucetpay-settings"
            className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50">
            <Save className="w-4 h-4" /> {loading === "save" ? "Saving..." : "Save Settings"}
          </button>
        </div>

        {/* Balance Check */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-[#F8FAFC]">FaucetPay Wallet Balances</h3>
            <button onClick={loadBalances} disabled={loading === "balance"} data-testid="refresh-balances-btn"
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest border border-[#00F0FF]/30 text-[#00F0FF] rounded-lg hover:bg-[#00F0FF]/10 disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${loading === "balance" ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          {!settings.has_key ? (
            <p className="text-sm text-[#94A3B8]">Set your API key above to view balances.</p>
          ) : balances ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(balances).map(([curr, data]) => (
                <div key={curr} className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30">
                  <p className="text-xs font-mono-game text-[#64748B] tracking-wider mb-1">{curr}</p>
                  <p className="font-mono-game text-lg font-bold text-[#FFD600]">{parseFloat(data.balance_coin || 0).toFixed(8)}</p>
                  <p className="text-xs text-[#64748B]">{parseInt(data.balance || 0).toLocaleString()} sat</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#94A3B8]">Click Refresh to load current balances.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
